import React, {
  Dispatch, ReactElement, SetStateAction, useEffect, useState,
} from 'react';
import {
  StyleSheet, Text, Pressable, View, FlexStyle,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import TemplateIcon from '../40pxIcons/TemplateIcon';
import AlertModal from '../AlertModal';
import AddTemplateModal from './AddTemplateModal';
import deleteTemplate from '../../requests/Assets/DeleteTemplate';
import { logEvent } from '../LogEvent';
import Style from '../../constants/Style';
import {
  UserPermission, Template, TemplateGroup, TemplateIssue, TemplateType, TemplateTypeTitles,
  UserGroup,
} from '../../constants/Types';
import SensorIcon24 from '../24pxIcons/SensorIcon24';
import MeterIcon from '../24pxIcons/MeterIcon';
import { DELETE_TEMPLATE_FAILURE } from '../../constants/AlertMessages';
import UserIcon24 from '../24pxIcons/UserIcon24';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import PressableStyled, { PressableState } from '../PressableStyled';
import EllipsisIconVertical from '../EllipsisIconVertical';
import getAssets from '../../requests/Assets/GetAssets';

interface TemplateRowWebProps {
  template: Template,
  closeMenu: () => void,
  showParentAlert,
  deleteAction,
  update,
  templateGroups: TemplateGroup[],
  userGroups: UserGroup[],
  setSelectedTitle: () => void,
  setSelected: () => void,
  setLinkAssetsView: () => void,
  index: number,
  style?: FlexStyle | FlexStyle[],
  optionsMenuTemplateId?: string,
  setOptionsMenuTemplateId?: Dispatch<SetStateAction<string | null>>,
}

export default function TemplateRowWebV2({
  template, closeMenu, showParentAlert, deleteAction, update, templateGroups, setSelected,
  userGroups, index, style, optionsMenuTemplateId, setOptionsMenuTemplateId,
}: TemplateRowWebProps) {
  const navigation = useNavigation();
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canEditTemplate: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_UPDATE);
  const canDeleteTemplate: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_DELETE);
  const optionsEnabled: boolean = canEditTemplate || canDeleteTemplate;
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>('');
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);
  const [assetCount, setAssetCount] = useState<number | null>(null);
  const optionsShown: boolean = optionsMenuTemplateId === template.id;

  useEffect(() => {
    setAssetCount(null);
    getAssets({
      templateId: template.id,
      locationId: buildingId,
      enableSubLocationsLookUp: true,
    }).then((res) => setAssetCount(res.assets.length)).catch(() => setAssetCount(null));
  }, [buildingId]);

  const showAlert = (
    title: string,
    message: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertVisible(true);
  };

  const deleteError = (text?: string) => showAlert(
    'Error',
    text ?? DELETE_TEMPLATE_FAILURE,
  );

  const deletePress: () => void = () => {
    closeMenu();
    logEvent('TemplateGroupRowWeb', 'Delete');
    showAlert(
      'Delete template',
      <>
        {'Are you sure you want to delete the template '}
        <Text style={Style.alertBoldText}>{template.name}</Text>
      </>,
      true,
      attemptDeletion,
      'Delete',
    );
  };

  const attemptDeletion: () => void = () => {
    setLoading(true);
    deleteTemplate(template.id).then((res: any) => {
      if (res && !res.isError) {
        showParentAlert(
          'Template successfully deleted',
          <Text>
            <Text style={Style.alertBoldText}>{template.name}</Text>
            {' was successfully deleted'}
          </Text>,
          false,
        );
        deleteAction(template.id);
      } else deleteError(res?.errorText);
    }).catch((error) => deleteError(error?.message))
      .finally(() => setLoading(false));
  };

  const renderIcon = (type: TemplateType) => {
    if (type === TemplateType.SENSOR) return <SensorIcon24 />;
    if (type === TemplateType.METER) return <MeterIcon stroke={'#fff'} />;
    return <TemplateIcon />;
  };

  const responsibleGroupIds: string[] = template.issues
    .reduce((all: string[], currentIssue: TemplateIssue) => {
      all.push(...currentIssue.UserGroupId);
      return all;
    }, []);
  const responsibleUserGroups: UserGroup[] = userGroups
    .filter((userGroup: UserGroup) => responsibleGroupIds.includes(userGroup.UserGroupId));
  const displayedUserGroups: UserGroup[] = responsibleUserGroups.slice(0, 3);

  const goToUserGroup: () => void = () => {
    navigation.navigate(AppScreen.USER_MANAGEMENT as any);
  };

  const showAssets: () => void = () => {
    navigation.navigate(
      AppScreen.ASSET_MANAGEMENT as any,
      { templateId: template.id, templateName: template.name },
    );
  };

  return <View style={[
    styles.container, { zIndex: index * -1 }, index % 2 === 0 && styles.containerAlternate, style,
  ]}>
    <View style={styles.header}>
      <View style={styles.icon}>
        {renderIcon(template.type)}
      </View>
      <View style={styles.nameSection}>
        <Text style={styles.title}>{template.name}</Text>
        <Text style={styles.type}>
          {TemplateTypeTitles[template.type] ?? TemplateTypeTitles[TemplateType.ASSET]}
        </Text>
      </View>
    </View>
    {false && <View style={styles.userGroups}>
      {displayedUserGroups.map((group: UserGroup, groupIndex: number) => <Pressable
        onPress={goToUserGroup}
        style={styles.tag}
        key={groupIndex}
      >
        <UserIcon24 />
        <Text style={styles.tagText}>{group.Name}</Text>
      </Pressable>)}
      {responsibleUserGroups.length > displayedUserGroups.length
        && <View style={styles.tag}>
          <Text style={styles.tagText}>
            {`+${responsibleUserGroups.length - displayedUserGroups.length}`}
          </Text>
        </View>}
    </View>}
    <View style={styles.countSection}>
      <PressableStyled
        onPress={showAssets}
        style={{ alignSelf: 'flex-start' }}
      >
        <Text style={styles.count}>
          {assetCount ?? '-'}
        </Text>
      </PressableStyled>
    </View>
    <View style={styles.actions}>
      {optionsEnabled && <>
        <PressableStyled
          onPress={() => {
            setOptionsMenuTemplateId(optionsMenuTemplateId === template.id ? null : template.id);
          }}
          style={styles.optionsButton}
        >
          <EllipsisIconVertical />
        </PressableStyled>
        <View>
          {optionsShown && <View style={styles.optionList}>
            {canEditTemplate && <Pressable
              style={({ hovered, pressed }: PressableState) => [
                styles.option, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
              ]}
              onPress={setSelected}
              testID='am-edit-template-btn'
            >
              <Text
                style={styles.optionText}
                selectable={false}
              >
                {'Edit'}
              </Text>
            </Pressable>}
            {canDeleteTemplate && <Pressable
              style={({ hovered, pressed }: PressableState) => [
                styles.option, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
              ]}
              onPress={deletePress}
              testID='am-delete-template-btn'
            >
              <Text
                style={styles.optionText}
                selectable={false}
              >
                {'Delete'}
              </Text>
            </Pressable>}
          </View>}
        </View>
      </>}
    </View>
    <AddTemplateModal
      templateGroups={templateGroups}
      isVisible={editModalVisible}
      hide={() => setEditModalVisible(false)}
      templateToUpdate={template}
      update={update}
      showParentAlert={showParentAlert}
    />
    <AlertModal
      modalVisible={alertVisible}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      action={alertAction}
      actionTitle={alertActionTitle}
      {...{ loading }}
      setModalVisible={setAlertVisible}
    />
  </View>;
}

const styles = StyleSheet.create({
  container: {
    ...Style.row,
    height: 60,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue200,
  },
  containerAlternate: {
    backgroundColor: Colors.light.hereworksWhite300,
  },
  icon: {
    marginStart: 18,
    marginEnd: 12,
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.light.hereworksDarkBlue,
  },
  nameSection: {
    justifyContent: 'center',
  },
  title: {
    color: Colors.light.hereworksPrimaryGrey,
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 16,
    lineHeight: 24,
  },
  type: {
    fontSize: 12,
    lineHeight: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  header: {
    flexDirection: 'row',
    flex: 3,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  userGroups: {
    flex: 2,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  tag: {
    ...Style.row,
    backgroundColor: Colors.light.hereworksDarkBlue,
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginEnd: 8,
    marginVertical: 2,
  },
  tagText: {
    color: '#FFF',
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 24,
    marginHorizontal: 4,
  },
  optionsButton: {
    paddingHorizontal: 16,
  },
  optionList: {
    position: 'absolute',
    ...Style.dropdownMenu,
    end: 16,
    top: 4,
    width: 87,
  },
  option: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  optionText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  actions: {
    flex: 1,
    alignItems: 'flex-end',
  },
  countSection: {
    flex: 3,
  },
  count: {
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    letterSpacing: 0,
    color: Colors.light.hereworksBlue500,
    textDecorationLine: 'underline',
  },
});
